import { db } from '$db/local/db';
import type { OutboxOperation } from '$db/local/db';
import { getActiveUserId } from '$core/identity/active-user';
import {
  OUTBOX_MAX_OPS,
  OUTBOX_MAX_BYTES,
  getOutboxStats,
  runCoalescence
} from '../repositories/outbox.repo';
import { quarantineOperation } from '../repositories/quarantine.repo';
import { coalesceOutbox } from './coalesce';

export type CapacityLayer = 'none' | 'coalesce' | 'squash' | 'quarantine';

export interface CapacityEnforcementResult {
  layer: CapacityLayer;
  dropped: number;
  quarantined: number;
  beforeOps: number;
  beforeBytes: number;
  afterOps: number;
  afterBytes: number;
}

export interface CapacityOptions {
  maxOps?: number;
  maxBytes?: number;
}

/**
 * Enforcement de cota de la outbox (RE-23.2).
 * Tres capas aplicadas en orden:
 *   1. Coalescencia normal (pending).
 *   2. Squash por entidad (incluye failed, consolida un snapshot).
 *   3. Cuarentena por antigüedad (no toca syncing).
 */
export async function enforceCapacity(
  options: CapacityOptions = {}
): Promise<CapacityEnforcementResult> {
  const maxOps = options.maxOps ?? OUTBOX_MAX_OPS;
  const maxBytes = options.maxBytes ?? OUTBOX_MAX_BYTES;

  const initial = await getOutboxStats();
  const beforeOps = initial.count;
  const beforeBytes = initial.bytes;

  if (initial.count <= maxOps && initial.bytes <= maxBytes) {
    return {
      layer: 'none',
      dropped: 0,
      quarantined: 0,
      beforeOps,
      beforeBytes,
      afterOps: initial.count,
      afterBytes: initial.bytes
    };
  }

  const coalesceRun = await runCoalescence();
  const afterCoalesce = await getOutboxStats();
  if (afterCoalesce.count <= maxOps && afterCoalesce.bytes <= maxBytes) {
    return {
      layer: 'coalesce',
      dropped: coalesceRun.dropped,
      quarantined: 0,
      beforeOps,
      beforeBytes,
      afterOps: afterCoalesce.count,
      afterBytes: afterCoalesce.bytes
    };
  }

  const squashDropped = await squashOutbox();
  const afterSquash = await getOutboxStats();
  if (afterSquash.count <= maxOps && afterSquash.bytes <= maxBytes) {
    return {
      layer: 'squash',
      dropped: coalesceRun.dropped + squashDropped,
      quarantined: 0,
      beforeOps,
      beforeBytes,
      afterOps: afterSquash.count,
      afterBytes: afterSquash.bytes
    };
  }

  const quarantined = await quarantineOldest(maxOps, maxBytes);
  const final = await getOutboxStats();
  return {
    layer: 'quarantine',
    dropped: coalesceRun.dropped + squashDropped,
    quarantined,
    beforeOps,
    beforeBytes,
    afterOps: final.count,
    afterBytes: final.bytes
  };
}

/**
 * Capa 2. Consolida también ops en 'failed' además de 'pending'.
 * Preserva 'syncing' (en vuelo) y 'quarantined'.
 */
async function squashOutbox(): Promise<number> {
  const userId = getActiveUserId();
  return db.transaction('rw', db.outbox, async () => {
    const all = await db.outbox.where('userId').equals(userId).toArray();
    const consolidables = all.filter(
      (o) => o.status === 'pending' || o.status === 'failed'
    );
    const preserved = all.filter(
      (o) => o.status === 'syncing' || o.status === 'quarantined'
    );

    if (consolidables.length <= 1) return 0;

    const asPending: OutboxOperation[] = consolidables.map((o) => ({
      ...o,
      status: 'pending'
    }));

    const result = coalesceOutbox(asPending);
    if (result.droppedCount === 0) return 0;

    await db.outbox.where('userId').equals(userId).delete();
    if (result.ops.length > 0) await db.outbox.bulkAdd(result.ops);
    if (preserved.length > 0) await db.outbox.bulkAdd(preserved);
    return result.droppedCount;
  });
}

/**
 * Capa 3. Mueve a cuarentena las ops más antiguas hasta bajar del límite.
 * Nunca toca ops en 'syncing'.
 */
async function quarantineOldest(
  maxOps: number,
  maxBytes: number
): Promise<number> {
  const userId = getActiveUserId();
  return db.transaction('rw', [db.outbox, db.quarantine], async () => {
    const all = await db.outbox.where('userId').equals(userId).toArray();
    const sorted = [...all].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    );

    let count = sorted.length;
    let bytes = sorted.reduce((s, o) => s + (o.bytes ?? 0), 0);
    let quarantined = 0;

    for (const op of sorted) {
      if (count <= maxOps && bytes <= maxBytes) break;
      if (op.status === 'syncing') continue;

      await quarantineOperation({
        entityType: op.entityType,
        entityId: op.entityId,
        op: op.op,
        payload: op.payload,
        schemaVersion: op.schemaVersion ?? 1,
        reason: 'capacity_exceeded',
        bytes: op.bytes ?? 0
      });
      await db.outbox.delete(op.id);
      count -= 1;
      bytes -= op.bytes ?? 0;
      quarantined += 1;
    }

    return quarantined;
  });
}